import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../contexts/auth/Context";
import { useToast } from "../../../contexts/toast/Context";
import {
  getCurrentUserGymExpensesConfigRequest,
  updateCurrentUserGymExpensesConfigRequest,
} from "../../requests/admin/gym";
import {
  gymExpensesConfigSchema,
  type ExpenseCategoryKey,
  type ExpenseClassificationValue,
  type GymExpensesConfig,
} from "../../../utils/zod/expensesConfig";
import {
  GYM_EXPENSES_CONFIG_QUERY_KEY,
  MONTHLY_EXPENSE_METRICS_QUERY_KEY,
  PRODUCTION_METRICS_QUERY_KEY,
} from "../../../query/keys";

export const useGymExpensesConfig = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { showToast } = useToast();

  const { data, isLoading, isError } = useQuery({
    queryKey: GYM_EXPENSES_CONFIG_QUERY_KEY,
    queryFn: () => getCurrentUserGymExpensesConfigRequest(token!),
    enabled: !!token,
    select: (data) => gymExpensesConfigSchema.parse(data),
  });
  const config: GymExpensesConfig | null = data ?? null;

  const {
    mutate: setCategory,
    isPending,
    variables,
  } = useMutation({
    mutationFn: ({
      category,
      value,
    }: {
      category: ExpenseCategoryKey;
      value: ExpenseClassificationValue;
    }) =>
      updateCurrentUserGymExpensesConfigRequest(
        { categoryClassification: { [category]: value } },
        token!,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(
        GYM_EXPENSES_CONFIG_QUERY_KEY,
        gymExpensesConfigSchema.parse(data),
      );
      // La clasificación afecta solo expense-summary y monthly-comparison
      // dentro de production-metrics. Usamos removeQueries (no invalidate)
      // porque las queries históricas tienen staleTime: Infinity +
      // refetchOnMount: false. Lo envolvemos en try/catch para no
      // interrumpir el feedback al usuario si hay falla local del cache.
      try {
        queryClient.removeQueries({
          queryKey: [...PRODUCTION_METRICS_QUERY_KEY, "expense-summary"],
        });
        queryClient.removeQueries({
          queryKey: [...PRODUCTION_METRICS_QUERY_KEY, "monthly-comparison"],
        });
        queryClient.invalidateQueries({
          queryKey: MONTHLY_EXPENSE_METRICS_QUERY_KEY,
        });
      } catch (e) {
        console.warn(
          "Cache invalidation failed after expensesConfig update",
          e,
        );
      }
      showToast("Configuración de gastos actualizada", "success");
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Error al actualizar la configuración de gastos";
      showToast(message, "error");
    },
  });

  const savingCategory: ExpenseCategoryKey | null = isPending
    ? (variables?.category ?? null)
    : null;

  return {
    config,
    isLoading,
    isError,
    savingCategory,
    setCategory,
  };
};
